import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { AppState } from '../../RootReducer';
import { CasinoActionTypes, selectFilter, selectGamesByFilter } from '../CasinoActions';
import { getSearchValue, getSelectedPageId } from '../../selectors/casino-selectors';

interface FilterParams {
	filterProviders: (ids: number[]) => void;
	filterFeatures: (ids: number[]) => void;
	selectedProviders: number[];
	selectedFeatures: number[];
}

export enum FilterType {
	PROVIDERS = 'providers',
	FEATURES = 'features'
}

export const getSelectedFilterIds = (key: string) => {
	const searchParams = new URLSearchParams(window.location.search);
	const providers = searchParams.get(key);
	return providers?.split(',').map(id => parseInt(id)) ?? [];
}

export const useGetSelectedFilterIds = () => {
	const location = useLocation();

	return useCallback((key: string, params?: URLSearchParams ) => {
		const searchParams = params ?? new URLSearchParams(location.search);
		const providers = searchParams.get(key);
		return providers?.split(',').map(id => parseInt(id)) ?? [];
	}, [location.search]);
}

export const useCasinoFilterParams = (): FilterParams => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const getSelectedFilterIds = useGetSelectedFilterIds();
	const state = useSelector((state: AppState) => state);
	const search = useSelector((state: AppState) => getSearchValue(state));
	const pageId = useSelector((state: AppState) => getSelectedPageId(state));
	const selectedProviders = useMemo(() => getSelectedFilterIds(FilterType.PROVIDERS), [getSelectedFilterIds]);
	const selectedFeatures = useMemo(() => getSelectedFilterIds(FilterType.FEATURES), [getSelectedFilterIds]);

	const onParamChange = useCallback(async (key: string, ids: number[]) => {
		dispatch(selectFilter())
		const params = new URLSearchParams(location.search);

		if(ids.length === 0) {
			params.delete(key);
			history.push({...location, search: params.toString()});
		} else {
			params.set(key, ids.join(','));
			history.push({
				...location,
				search: params.toString()
			});
		}

		const providers = getSelectedFilterIds(FilterType.PROVIDERS, params);
		const features = getSelectedFilterIds(FilterType.FEATURES, params);
		const games = await selectGamesByFilter(state, providers, features, search, pageId);
        dispatch({type: CasinoActionTypes.SET_SELECTED_GAMES, games});
	}, [history, location, dispatch, pageId, search, state, getSelectedFilterIds])

	useEffect(() => {
		// This effect is used to filter games when a page is loaded with filter params(reloaded page etc.)
		const providers = getSelectedFilterIds(FilterType.PROVIDERS);
		const features = getSelectedFilterIds(FilterType.FEATURES);

		if(providers.length === 0 && features.length === 0) return;

		selectGamesByFilter(state, providers, features, search, pageId).then(games => {
			dispatch({type: CasinoActionTypes.SET_SELECTED_GAMES, games});
		});
	}, [pageId, location.search]);

	return {
		filterProviders: (ids: number[]) => onParamChange(FilterType.PROVIDERS, ids),
		filterFeatures: (ids: number[]) => onParamChange(FilterType.FEATURES, ids),
		selectedProviders,
		selectedFeatures
	};
};

export const useClearAllFilterParams = () => {
	const history = useHistory();
	const location = useLocation();

	const clearAllFilterParams = useCallback(() => {
		history.push({
			...location,
			search: ''
		});
	}, []);

	return clearAllFilterParams;
}

export const useIsFilterParamsActive = () => {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	return params.has(FilterType.PROVIDERS) || params.has(FilterType.FEATURES);
}
